import { Button } from '@dev-spendesk/grapes';
import { useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { VerticalLine } from 'modules/requests/components/common/VerticalLine';
import { getTripDates } from 'modules/requests/utils/trips';
import { StayResultOverview } from 'modules/trips/components/StayResultOverview/StayResultOverview';
import { StaysSearchEditModal } from 'modules/trips/components/StaysSearchEditModal/StaysSearchEditModal';
import { TravelPageWrapper } from 'modules/trips/components/TravelPageWrapper/TravelPageWrapper';
import {
  type TravelSearchState,
  isTravelSearchState,
} from 'modules/trips/models/TravelSearchState';
import { CalloutError } from 'src/core/common/components/LazyLoad';
import { routeFor, routes } from 'src/core/constants/routes';
import history from 'src/core/history';

import { StayResultsSkeleton } from '../../components/StayResultsSkeleton/StayResultsSkeleton';
import { usePropertiesSessionResultsQuery } from '../../hooks/usePropertiesSessionResultsQuery';

export const TravelSearchResultsPage = () => {
  const company = useCompany();
  const { t, localeFormat } = useTranslation('global');
  const [isEditSearchModalOpen, setIsEditSearchModalOpen] = useState(false);

  const state = isTravelSearchState(history.location.state)
    ? (history.location.state as TravelSearchState)
    : undefined;

  const { isLoading, isError, isStatusComplete, results, refetch, progress } =
    usePropertiesSessionResultsQuery(state?.searchId);

  if (!state) {
    history.replace(
      routeFor(routes.TRAVEL_HOMEPAGE.path, {
        company: company.id,
      }),
    );
    return null;
  }

  return (
    <>
      <TravelPageWrapper
        onBack={() =>
          history.push(
            routeFor(routes.TRAVEL_HOMEPAGE.path, {
              company: company.id,
            }),
          )
        }
        title={t('travel.hotel.search.resultsTitle')}
        isLoading={isLoading}
        progress={progress}
        headerDetails={
          state.location && state.checkin && state.checkout ? (
            <div className="flex items-center">
              {state.location.name}
              <VerticalLine
                className="text-content-disabled mx-8"
                height={14}
              />
              {getTripDates(state.checkin, state.checkout, localeFormat)}
              <Button
                className="ml-16"
                text={t('travel.hotel.search.editSearch')}
                variant="secondaryNeutral"
                onClick={() => setIsEditSearchModalOpen(true)}
              />
            </div>
          ) : undefined
        }
      >
        <div className="flex gap-24">
          <div className="mx-40 my-24 flex flex-1 flex-col items-center gap-24">
            {isLoading && <StayResultsSkeleton />}
            {isError && (
              <CalloutError
                className="min-w-[400px] self-baseline"
                retry={() => {
                  refetch();
                }}
              />
            )}
            {isStatusComplete &&
              results.map((item) => {
                return (
                  <StayResultOverview
                    key={item.propertyId}
                    stay={{
                      propertyId: item.propertyId,
                      name: item.name,
                      image: item.image ?? '', // TODO: image is not supported yet in the API
                      rating: item.rating,
                      distance: item.distance ?? 0, // TODO: distance is not supported yet in the API
                      price: item.price,
                      fewRoomsLeft: item.fewRoomsLeft,
                    }}
                    onSelect={() => {
                      history.push(
                        routeFor(routes.PROPERTY_DETAILS.path, {
                          company: company.id,
                          id: item.propertyId,
                        }),
                        state,
                      );
                    }}
                  />
                );
              })}
          </div>
        </div>
      </TravelPageWrapper>
      <StaysSearchEditModal
        isOpen={isEditSearchModalOpen}
        onClose={() => setIsEditSearchModalOpen(false)}
        initialState={state}
      />
    </>
  );
};
